/**
 * Starts a download of multiple assets in a zip file by clicking an element (typically a button).
 *
 * This component uses the "render prop" approach. The button used to open the popup should be
 * rendered within a function passed in as props.children (aka render prop). The actual element that
 * the user clicks needs to use buttonProps.
 *
 * <MultiAssetDownload items={...}>
 *   {({ buttonProps }) => {
 *     return <Button {...buttonProps} text="Download" />
 *   }}
 * </MultiAssetDownload>;
 *
 * Also provided to the render function is numItems, which gives how many items are set to be
 * downloaded (and excludes non-downloadable items, such as colors)
 *
 * <MultiAssetDownload items={...}>
 *   {({ buttonProps, numItems }) => {
 *     return (
 *        <Button
 *          {...buttonProps}
 *          text={`Download ${numItems} Files`}
 *        />
 *      );
 *   }}
 * </MultiAssetDownload>;
 */

import React, { useCallback, useMemo } from "react";
import { AssetType, SpacePermission, ItemType } from "@thenounproject/lingo-core";

import { Inspectable } from "@constants/Inspector";
import useDownloadMultipleAssets from "@actions/useDownloadMultipleAssets";
import useNotifications from "@actions/useNotifications";
import { useSelectSpace } from "@selectors/entities/spaces";
import { useDownloadErrorHandler } from "@features/asset-export/hooks/useDownloadErrorHandler";

type RenderProps = {
  buttonProps: {
    onClick: () => void;
  };
  numItems: number;
};

type Props = {
  inspectables: Inspectable[];
  children: (renderProps: RenderProps) => React.ReactNode;
};

export const useMultiAssetDownload = (inspectables: Inspectable[]) => {
  const [downloadMultipleAssets] = useDownloadMultipleAssets();
  const { showNotification } = useNotifications();
  const { handleDownloadError } = useDownloadErrorHandler();
  const space = useSelectSpace();

  const filteredAssetInspectables = useMemo(() => {
    return inspectables.filter(i => {
      const isAsset = !i.item || i.item.type === ItemType.asset;
      return Boolean(isAsset && i?.asset?.permalink && i?.asset?.type !== AssetType.URL);
    });
  }, [inspectables]);

  const downloadAssets = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) {
        const event = e;
        event.preventDefault();
        event.stopPropagation();
      }
      showNotification({
        message: `Preparing to download ${filteredAssetInspectables.length} assets.`,
      });

      const useLibrary =
        space?.access?.permissions?.includes(SpacePermission.viewAssetLibrary) === true;

      const action = await downloadMultipleAssets({
        inspectables: filteredAssetInspectables,
        isAdmin: useLibrary,
      });

      if (action.error) {
        const handled = handleDownloadError(action.error, {
          onRetry: () => downloadAssets(),
        });

        if (!handled) {
          showNotification({
            level: "error",
            message:
              action.error.message ||
              "Unfortunately, there was a problem starting your download.  Please try again later.",
          });
        }
      }
    },
    [
      showNotification,
      filteredAssetInspectables,
      space?.access?.permissions,
      downloadMultipleAssets,
      handleDownloadError,
    ]
  );
  return { downloadAssets, assetCount: filteredAssetInspectables.length };
};

// TODO: Remove this component and use the hook directly in the kits
export default function MultiAssetDownload({ inspectables, children }: Props) {
  const { downloadAssets, assetCount } = useMultiAssetDownload(inspectables);
  return (
    <>
      {children({
        buttonProps: { onClick: downloadAssets },
        numItems: assetCount,
      })}
    </>
  );
}
